import { Position } from "@/services/backend/rlt/treatments/types/position";
import { HTMLAttributes, useEffect, useState } from "react";
import { cn } from "@/shared/lib/utils";
import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { useTreatmentComponentState } from "@/services/backend/rlt/treatments/components/treatment-component-state-context";
import { FinalPriceCell } from "@/services/backend/rlt/treatments/components/final-price-cell";
import {
  ALL_COLUMNS,
  Column,
} from "@/services/backend/rlt/treatments/lib/treatment-component-state";
import { BasePriceCell } from "@/services/backend/rlt/treatments/components/base-price-cell";
import { CycleCell } from "@/services/backend/rlt/treatments/components/cycle-cell";
import { CycleDiscountCell } from "@/services/backend/rlt/treatments/components/cycle-discount-cell";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import t from "@/lang/lang";
import {
  useRltTreatmentAddPositionMutation,
  useRltTreatmentDeletePositionMutation,
} from "@/services/backend/rlt/treatments/api/treatment-api";
import { Kind } from "@/services/backend/rlt/treatments/types/component";
import { v4 } from "uuid";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { usePersistenceContext } from "@/shared/lib/persistence-state/context";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";
import { CopyPositionDialog } from "@/services/backend/rlt/treatments/components/copy-position-dialog";
import { SpecialDiscountCell } from "@/services/backend/rlt/treatments/components/special-discount-cell";
import { DeletePositionDialog } from "./delete-position-dialog";
import { CompletedCell } from "./completed-cell";
import { CommentCell } from "./comment-cell";
import { CleanTimeCell } from "./clean-time-cell";
import { FormulaCell } from "./formula-cell";
import { ComponentCell } from "./component-cell";
import { KindCell } from "./kind-cell";

export function PositionRow({
  treatmentId,
  position,
}: {
  treatmentId: string;
  position: Position;
}) {
  const { state, disabled } = useTreatmentComponentState();
  const { columns } = state;

  // Components forming the air handling unit itself
  const ahuComponents = [...(position.children ?? [])].filter((p) =>
    componentsThatFormAhu.includes(p.component.kind),
  );

  // Components that are ducts belonging to the air handling unit
  const ahuDuctComponents = [...(position.children ?? [])].filter(
    (p) => !componentsThatFormAhu.includes(p.component.kind),
  );

  return (
    <>
      <tr className="border-t align-top">
        {ALL_COLUMNS.map((column) => {
          if (!columns.includes(column)) {
            return null;
          }
          return (
            <Cell
              key={column}
              column={column}
              treatmentId={treatmentId}
              position={position}
              disabled={disabled}
            />
          );
        })}
      </tr>
      {ahuComponents.length > 0 && (
        <tr className="align-top">
          {}
          <td />
          <td className="pl-6 font-bold">{t("Anlagenkomponenten:")}</td>
          {}
          <td colSpan={columns.length - 2} />
        </tr>
      )}
      {ahuComponents.sort(byKind).map((child) => (
        <tr className="align-top" key={child.id}>
          {ALL_COLUMNS.map((column) => {
            if (!columns.includes(column)) {
              return null;
            }
            return (
              <ChildCell
                key={column}
                column={column}
                treatmentId={treatmentId}
                position={child}
                disabled={disabled}
              />
            );
          })}
        </tr>
      ))}
      {ahuDuctComponents.length > 0 && (
        <tr className="align-top">
          {}
          <td />
          <td className="pl-6 font-bold">{t("Leitungen zum System:")}</td>
          {}
          <td colSpan={columns.length - 2} />
        </tr>
      )}
      {ahuDuctComponents.sort(byKind).map((child) => (
        <tr className="align-top" key={child.id}>
          {ALL_COLUMNS.map((column) => {
            if (!columns.includes(column)) {
              return null;
            }
            return (
              <ChildCell
                key={column}
                column={column}
                treatmentId={treatmentId}
                position={child}
                disabled={disabled}
              />
            );
          })}
        </tr>
      ))}
      {position.component.canHaveChildren && !disabled && (
        <tr>
          {}
          <td />
          {}
          <td colSpan={ALL_COLUMNS.length - 1} className="px-6 pt-2 pb-3">
            <AddPositionDropdown treatmentId={treatmentId} parent={position} />
          </td>
        </tr>
      )}
    </>
  );
}

const componentsThatFormAhu = [
  Kind.AirHandlingUnit,
  Kind.CrossFlowHeatExchanger,
  Kind.RecirculatingSprayHumidifier,
  Kind.RotaryHeatExchanger,
];

function byKind(a: Position, b: Position) {
  const aKind = t(a.component.kind);
  const bKind = t(b.component.kind);
  return aKind.localeCompare(bKind);
}

function Cell({
  treatmentId,
  column,
  position,
  disabled = false,
}: {
  treatmentId: string;
  column: Column;
  position: Position;
  disabled?: boolean;
}) {
  switch (column) {
    case Column.Position:
      return (
        <TD className="font-bold">
          <span className="flex h-6 items-center">{position.number}</span>
        </TD>
      );
    case Column.Completed:
      return (
        <TD className="flex justify-start">
          <CompletedCell
            treatmentId={treatmentId}
            position={position}
            className="h-5 w-5"
          />
        </TD>
      );
    case Column.ComponentKind:
      return (
        <TD>
          <KindCell kind={position.component.kind} />
        </TD>
      );
    case Column.ComponentData:
      return (
        <TD>
          <ComponentCell
            treatmentId={treatmentId}
            positionId={position.id}
            component={position.component}
            disabled={disabled}
          />
        </TD>
      );
    case Column.Cycle:
      return (
        <TD className="text-right">
          <CycleCell
            treatmentId={treatmentId}
            position={position}
            disabled={disabled}
          />
        </TD>
      );
    case Column.Formula:
      return (
        <TD>
          <FormulaCell
            treatmentId={treatmentId}
            positionId={position.id}
            component={position.component}
            disabled={disabled}
          />
        </TD>
      );
    case Column.CleanTime:
      return (
        <TD className="text-right">
          <CleanTimeCell position={position} />
        </TD>
      );
    case Column.BasePrice:
      return (
        <TD className="text-right">
          <BasePriceCell position={position} />
        </TD>
      );
    case Column.CycleDiscount:
      return (
        <TD className="text-right">
          <CycleDiscountCell
            treatmentId={treatmentId}
            position={position}
            disabled={disabled}
          />
        </TD>
      );
    case Column.SpecialDiscount:
      return (
        <TD className="text-right">
          <SpecialDiscountCell
            treatmentId={treatmentId}
            position={position}
            disabled={disabled}
          />
        </TD>
      );
    case Column.FinalPrice:
      return (
        <TD className="text-right">
          <FinalPriceCell position={position} />
        </TD>
      );
    case Column.ActuallyTreated:
      return (
        <TD>
          <ComponentCell
            treatmentId={treatmentId}
            positionId={position.id}
            component={position.actuallyTreated}
            actuallyTreated
            disabled={disabled}
          />
        </TD>
      );
    case Column.Comment:
      return (
        <TD>
          <CommentCell
            treatmentId={treatmentId}
            positionId={position.id}
            comment={position.comment}
            disabled={disabled}
          />
        </TD>
      );
    case Column.Actions:
      return (
        <TD className="flex justify-end space-x-2 px-3 py-2">
          {!disabled && (
            <>
              <CopyPositionDialog
                treatmentId={treatmentId}
                position={position}
              />
              <DeletePositionDialog
                treatmentId={treatmentId}
                position={position}
              />
            </>
          )}
        </TD>
      );
    default:
      return null;
  }
}

type TDProps = HTMLAttributes<HTMLTableCellElement>;

function TD({ children, className }: TDProps) {
  return <th className={cn("px-6 py-3 font-normal", className)}>{children}</th>;
}

function ChildCell({
  treatmentId,
  column,
  position,
  disabled = false,
}: {
  treatmentId: string;
  column: Column;
  position: Position;
  disabled?: boolean;
}) {
  switch (column) {
    case Column.Position:
      return <TD />;
    case Column.Completed:
      return <TD />;
    case Column.ComponentKind:
      return (
        <TD className="flex h-9 items-center justify-between py-1.5">
          <KindCell kind={position.component.kind} className="font-normal" />
          {!disabled && (
            <DeleteChildPositionDialog
              treatmentId={treatmentId}
              position={position}
            />
          )}
        </TD>
      );
    case Column.ComponentData:
      return (
        <TD className="py-1.5">
          <ComponentCell
            treatmentId={treatmentId}
            positionId={position.id}
            component={position.component}
            disabled={disabled}
          />
        </TD>
      );
    case Column.Cycle:
      return <TD />;
    case Column.Formula:
      return (
        <TD className="py-1.5">
          <FormulaCell
            treatmentId={treatmentId}
            positionId={position.id}
            component={position.component}
            disabled={disabled}
          />
        </TD>
      );
    case Column.CleanTime:
      return (
        <TD className="py-1.5 text-right">
          <CleanTimeCell position={position} className="font-normal" />
        </TD>
      );
    case Column.BasePrice:
      return (
        <TD className="py-1.5 text-right">
          <BasePriceCell position={position} className="font-normal" />
        </TD>
      );
    case Column.FinalPrice:
      return (
        <TD className="py-1.5 text-right">
          <FinalPriceCell position={position} />
        </TD>
      );
    case Column.CycleDiscount:
      return <TD />;
    case Column.ActuallyTreated:
      return (
        <TD className="py-1.5">
          <ComponentCell
            treatmentId={treatmentId}
            positionId={position.id}
            component={position.actuallyTreated}
            actuallyTreated
            disabled={disabled}
          />
        </TD>
      );
    case Column.Comment:
      return <TD />;
    case Column.Actions:
      return <TD />;
    default:
      return null;
  }
}

function AddPositionDropdown({
  treatmentId,
  parent,
}: {
  treatmentId: string;
  parent: Position;
}) {
  const [add, { isLoading, error, reset }] =
    useRltTreatmentAddPositionMutation();
  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error, false),
        variant: "destructive",
      });
      reset();
    }
  }, [error, toast, reset]);

  const doAdd = (kind: Kind) => {
    if (isLoading) {
      return;
    }

    add({
      id: treatmentId,
      positionId: v4(),
      parentId: parent.id,
      segment: parent.segmentKind,
      area: parent.area,
      componentKind: kind,
    });
  };

  // Components forming the air handling unit itself
  const ahuComponents = parent.component.permittedChildrenKind.filter((k) =>
    componentsThatFormAhu.includes(k),
  );

  // Components that are ducts belonging to the air handling unit
  const ahuDuctComponents = parent.component.permittedChildrenKind.filter(
    (k) => !componentsThatFormAhu.includes(k),
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="secondary">
          <span>{t("Bauteil hinzufügen")}</span>
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>{t("Anlagenkomponenten")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {[...ahuComponents].sort(byTranslation).map((kind) => (
            <DropdownMenuItem
              key={kind}
              className="cursor-pointer items-center"
              onClick={() => doAdd(kind)}
            >
              <Plus className="mr-2 h-4 w-4" />
              <span>{t(kind)}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t("Leitungen")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {[...ahuDuctComponents].sort(byTranslation).map((kind) => (
            <DropdownMenuItem
              key={kind}
              className="cursor-pointer items-center"
              onClick={() => doAdd(kind)}
            >
              <Plus className="mr-2 h-4 w-4" />
              <span>{t(kind)}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function byTranslation(a: Kind, b: Kind) {
  const aT = t(a);
  const bT = t(b);
  return aT.localeCompare(bT);
}

function DeleteChildPositionDialog({
  treatmentId,
  position,
}: {
  treatmentId: string;
  position: Position;
}) {
  const [open, setOpen] = useState<boolean>(false);
  const [deletePosition, { isLoading, error, isSuccess, reset }] =
    useRltTreatmentDeletePositionMutation();
  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error, false),
        variant: "destructive",
      });
      reset();
    }
  }, [error, toast, reset]);

  if (isSuccess) {
    setOpen(false);
    reset();
  }

  const doDelete = () => {
    if (isLoading) {
      return;
    }

    deletePosition({
      id: treatmentId,
      positionId: position.id,
    });
  };

  const { setPersistenceState } = usePersistenceContext();
  useEffect(() => {
    if (isLoading) {
      setPersistenceState(PersistenceState.SAVING);
    } else if (error) {
      setPersistenceState(PersistenceState.ERROR);
    } else {
      setPersistenceState(PersistenceState.SAVED);
    }
  }, [isLoading, error, setPersistenceState]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost">
          <Trash2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(`${t(position.component.kind)} wirklich löschen?`)}
          </DialogTitle>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button variant="destructive" onClick={doDelete}>
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
